import { useEffect, useState, useRef } from "react";
import { Link } from "react-router-dom";
import { motion, AnimatePresence, useScroll, useTransform } from "framer-motion";
import { ArrowUpRight, ArrowRight } from "lucide-react";
import { RevealLines, Reveal } from "@/components/Reveal";
import Marquee from "@/components/Marquee";
import JobCard from "@/components/JobCard";
import Partners from "@/components/Partners";
import { useLanguage } from "@/context/LanguageContext";
import { IMAGES, PAGE_META } from "@/data/content";
import usePageMeta from "@/hooks/usePageMeta";
import { publicApi as api } from "@/lib/api";

export default function Home() {
  const { t, lang } = useLanguage();
  usePageMeta({ ...PAGE_META[lang].home, lang, image: IMAGES.heroSlides[0] });
  const [jobs, setJobs] = useState([]);
  const [slide, setSlide] = useState(0);
  const heroRef = useRef(null);
  const { scrollYProgress } = useScroll({ target: heroRef, offset: ["start start", "end start"] });
  const y = useTransform(scrollYProgress, [0, 1], ["0%", "30%"]);
  const scale = useTransform(scrollYProgress, [0, 1], [1, 1.15]);
  const opacity = useTransform(scrollYProgress, [0, 0.8], [1, 0]);

  useEffect(() => {
    const id = setInterval(() => setSlide((p) => (p + 1) % IMAGES.heroSlides.length), 5200);
    return () => clearInterval(id);
  }, []);

  const SECTORS = [
    { name: t.sectors.names.pharma, img: IMAGES.sectors.pharma, span: "md:col-span-7" },
    { name: t.sectors.names.medtech, img: IMAGES.sectors.medtech, span: "md:col-span-5" },
    { name: t.sectors.names.food, img: IMAGES.sectors.food, span: "md:col-span-5" },
    { name: t.sectors.names.aerodef, img: IMAGES.sectors.aerodef, span: "md:col-span-7" },
    { name: t.sectors.names.chimie, img: IMAGES.sectors.chimie, span: "md:col-span-7" },
    { name: t.sectors.names.industrie, img: IMAGES.sectors.industrie, span: "md:col-span-5" },
  ];
  const marqueeItems = Object.values(t.sectors.names);

  useEffect(() => {
    api.get("/jobs").then((r) => setJobs(r.data.slice(0, 3))).catch(() => {});
  }, []);

  return (
    <div>
      {/* HERO */}
      <section ref={heroRef} className="relative min-h-screen flex items-end overflow-hidden">
        <motion.div style={{ y, scale }} className="absolute inset-0 z-0">
          <AnimatePresence>
            <motion.img
              key={slide}
              src={IMAGES.heroSlides[slide]}
              alt=""
              initial={{ opacity: 0, scale: 1.06 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 1.6, ease: "easeInOut" }}
              className="absolute inset-0 w-full h-full object-cover"
            />
          </AnimatePresence>
          <div className="absolute inset-0 bg-y5-ink/70" />
          <div className="absolute inset-0 bg-gradient-to-t from-y5-ink via-y5-ink/40 to-transparent" />
        </motion.div>

        <motion.div style={{ opacity }} className="relative z-10 max-w-[1400px] mx-auto px-5 sm:px-6 lg:px-12 pb-24 w-full">
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.1 }} className="flex items-center gap-3 mb-8">
            <span className="h-px w-12 bg-y5-yellow" />
            <span className="text-y5-yellow text-sm uppercase tracking-[0.3em]">{t.hero.kicker}</span>
          </motion.div>

          <h1 className="font-display text-[8vw] md:text-[5.5vw] leading-[0.9] tracking-tight uppercase">
            <RevealLines lines={t.hero.lines} />
            <span className="hero-mask-line">
              <motion.span className="block" initial={{ y: "110%" }} animate={{ y: "0%" }} transition={{ duration: 0.9, ease: [0.16, 1, 0.3, 1], delay: 0.24 }}>
                {t.hero.line2}
              </motion.span>
            </span>
            <span className="hero-mask-line">
              <motion.span className="block text-y5-yellow" initial={{ y: "110%" }} animate={{ y: "0%" }} transition={{ duration: 0.9, ease: [0.16, 1, 0.3, 1], delay: 0.36 }}>
                {t.hero.line3}
              </motion.span>
            </span>
          </h1>

          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.7 }} className="flex flex-col md:flex-row md:items-center gap-6 mt-10">
            <p className="text-y5-muted text-lg max-w-md">{t.hero.subtitle}</p>
            <div className="flex flex-col sm:flex-row gap-3 sm:gap-4">
              <Link to="/offres" data-testid="hero-offres" className="group inline-flex items-center justify-center gap-2 bg-y5-yellow text-y5-ink px-7 py-3.5 font-semibold hover:scale-[1.03] transition-transform whitespace-nowrap">
                {t.hero.ctaPrimary} <ArrowRight size={18} className="group-hover:translate-x-1 transition-transform" />
              </Link>
              <Link to="/candidature" data-testid="hero-spontanee" className="inline-flex items-center justify-center gap-2 border border-white/30 px-7 py-3.5 hover:border-y5-yellow hover:text-y5-yellow transition-colors whitespace-nowrap">
                {t.hero.ctaSecondary}
              </Link>
            </div>
          </motion.div>
        </motion.div>
      </section>

      {/* STATS */}
      <section className="relative z-10 border-y border-y5-line bg-y5-ink">
        <div className="max-w-[1400px] mx-auto px-5 sm:px-6 lg:px-12 grid grid-cols-1 md:grid-cols-3">
          {t.stats.map((s, i) => (
            <Reveal key={s.l} delay={i * 0.08} className={`py-12 ${i !== 0 ? "border-t md:border-t-0 md:border-l border-y5-line" : ""}`}>
              <div className="px-6">
                <div className="font-display text-5xl md:text-6xl text-y5-yellow tracking-tight">{s.k}</div>
                <div className="text-y5-muted mt-2 text-sm uppercase tracking-widest">{s.l}</div>
              </div>
            </Reveal>
          ))}
        </div>
      </section>

      {/* INTRO STATEMENT */}
      <section className="relative z-10 py-28 md:py-36 border-b border-y5-line">
        <div className="max-w-[1400px] mx-auto px-5 sm:px-6 lg:px-12 grid grid-cols-1 lg:grid-cols-12 gap-12 items-start">
          <div className="lg:col-span-7">
            <h2 className="font-display text-4xl sm:text-5xl md:text-7xl tracking-tight uppercase leading-[0.95]">
              {t.intro.title.map((line, i) => (<span key={i} className="block">{line}</span>))}
            </h2>
          </div>
          <Reveal delay={0.1} className="lg:col-span-5">
            <p className="text-white/80 text-lg leading-relaxed">{t.intro.text}</p>
            <Link to="/services" data-testid="intro-services-link" className="inline-flex items-center gap-2 mt-8 text-white border-b border-y5-yellow pb-1 hover:gap-3 transition-all">
              {t.intro.link} <ArrowRight size={16} />
            </Link>
          </Reveal>
        </div>
      </section>

      {/* SECTORS BENTO */}
      <section className="relative z-10 py-28 md:py-36">
        <div className="max-w-[1400px] mx-auto px-5 sm:px-6 lg:px-12">
          <div className="flex flex-col md:flex-row md:items-end justify-between mb-14 gap-6">
            <div>
              <span className="text-y5-yellow text-sm uppercase tracking-[0.3em]">{t.sectors.kicker}</span>
              <h2 className="font-display text-4xl sm:text-5xl md:text-7xl tracking-tight mt-4 uppercase">
                {t.sectors.title.map((l, i) => (<span key={i} className="block">{l}</span>))}
              </h2>
            </div>
            <p className="text-y5-muted max-w-sm">{t.sectors.desc}</p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-12 gap-4">
            {SECTORS.map((s, i) => (
              <Reveal key={s.name} delay={i * 0.08} className={`${s.span} group relative h-[340px] overflow-hidden border border-y5-line`}>
                <img src={s.img} alt={s.name} className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110" />
                <div className="absolute inset-0 bg-gradient-to-t from-y5-ink via-y5-ink/30 to-transparent" />
                <div className="absolute bottom-0 left-0 p-8 flex items-end justify-between w-full">
                  <h3 className="font-display text-3xl md:text-4xl uppercase tracking-tight">{s.name}</h3>
                  <ArrowUpRight className="text-y5-yellow opacity-0 group-hover:opacity-100 transition-opacity" size={28} />
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <Marquee items={marqueeItems} />

      {/* MANIFESTO */}
      <section className="relative z-10 py-28 md:py-36">
        <div className="max-w-[1400px] mx-auto px-5 sm:px-6 lg:px-12">
          <h2 className="font-display text-4xl sm:text-5xl md:text-7xl tracking-tight uppercase mb-10 md:mb-16 max-w-3xl">
            {t.manifesto.title.map((l, i) => (<span key={i} className="block">{l}</span>))}
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-px bg-y5-line border border-y5-line">
            {t.manifesto.chapters.map((m, i) => (
              <Reveal key={m.n} delay={i * 0.1} className="bg-y5-ink p-10 hover:bg-y5-surface transition-colors">
                <div className="font-display text-6xl text-stroke mb-8">{m.n}</div>
                <h3 className="font-display text-2xl mb-4 leading-tight uppercase">{m.t}</h3>
                <p className="text-y5-muted leading-relaxed">{m.d}</p>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* LATEST JOBS */}
      {jobs.length > 0 && (
        <section className="relative z-10 pb-28 md:pb-36">
          <div className="max-w-[1400px] mx-auto px-5 sm:px-6 lg:px-12">
            <div className="flex items-end justify-between mb-12">
              <h2 className="font-display text-3xl sm:text-4xl md:text-6xl tracking-tight uppercase">{t.latestJobs.title}</h2>
              <Link to="/offres" className="hidden md:flex items-center gap-2 text-white border-b border-y5-yellow pb-1 hover:gap-3 transition-all">
                {t.latestJobs.all} <ArrowRight size={18} />
              </Link>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              {jobs.map((j, i) => <JobCard key={j.id} job={j} index={i} />)}
            </div>
          </div>
        </section>
      )}

      <Partners />

      {/* CTA */}
      <section className="relative z-10 border-t border-y5-line py-28 md:py-40 bg-y5-surface">
        <div className="max-w-[1400px] mx-auto px-5 sm:px-6 lg:px-12 text-center">
          <Reveal>
            <h2 className="font-display text-4xl sm:text-5xl md:text-8xl tracking-tight uppercase mb-8">
              {t.ctaHome.title[0]}<br /><span className="text-y5-yellow">{t.ctaHome.accent}</span>
            </h2>
            <p className="text-y5-muted text-lg max-w-xl mx-auto mb-10">{t.ctaHome.text}</p>
            <Link to="/contact" data-testid="cta-contact" className="inline-flex items-center gap-2 bg-y5-yellow text-y5-ink px-9 py-4 font-semibold hover:scale-[1.03] transition-transform">
              {t.ctaHome.button} <ArrowUpRight size={18} />
            </Link>
          </Reveal>
        </div>
      </section>
    </div>
  );
}
